import React, { useEffect, useRef, useState } from 'react';
import { Box, Grid, Paper, FormControlLabel, Checkbox, TextField, Typography, Alert, Chip, Stack, Select, MenuItem, Button, IconButton, FormControl, FormLabel, RadioGroup, Radio, FormHelperText } from '@mui/material';
import PodcastsIcon from '@mui/icons-material/Podcasts';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import EventIcon from '@mui/icons-material/Event';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import HeadsetIcon from '@mui/icons-material/Headset';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CollapsibleSection from '../../../components/common/CollapsibleSection';
import { getIssueCastCapture, IssueCastCaptureResponse } from '../../../api/apiClient';
import ImageUrlSelector from '../../../components/common/ImageUrlSelector';

// Minimal shape required from the parent form data for IssueCast
export type IssueCastFormData = {
  publisherPK?: string; // Added to support media library
  studioConfig?: {
    coHosts?: Array<{ name?: string; voiceId?: string; voiceType?: string; customVoiceId?: string }>;
  };
  issueCast?: {
    enabled: boolean;
    masthead?: {
      publisherLogoURL?: string;
      publisherSubdomain?: string;
      locationName?: string; // Added Location Name
      backgroundGradient?: string;
    };
    sponsor?: {
      imageUrl?: string;
      mention?: string;
      clickThroughUrl?: string;
      name?: string; // Added Sponsor Name field
    };
    // Added Audio Stream structure for Location Station ID
    audioStream?: {
      locationStationId?: string;
    };
    // New Weather configuration
    weather?: {
      enabled?: boolean;
      locationName?: string;
      latitude?: string | number;
      longitude?: string | number;
    };
    // New Events configuration
    events?: {
      enabled?: boolean;
      interests?: string; // comma-separated string in the form state
      usePublisherEvents?: boolean;
      useSearchEvents?: boolean;
    };
    // New Podcasts configuration
    podcasts?: Array<{
      type: string;
      url: string;
    }>;
    convai?: {
      enabled?: boolean;
      voiceId?: string;
      firstMessage?: string;
    };
  };
};

interface IssueCastTabProps {
  formData: IssueCastFormData;
  errors: Record<string, string>;
  isLoading: boolean;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onIssueCastEnabledChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

// Removed unused ComingSoonPlaceholder

const IssueCastTab: React.FC<IssueCastTabProps> = ({
  formData,
  errors,
  isLoading,
  onInputChange,
  onIssueCastEnabledChange,
}) => {
  // Local state for IssueCast fetch by ID
  const [issueCastResult, setIssueCastResult] = useState<IssueCastCaptureResponse | null>(null);
  const [issueCastError, setIssueCastError] = useState<string | null>(null);
  const [issueCastLoading, setIssueCastLoading] = useState<boolean>(false);
  const [generatingGradient, setGeneratingGradient] = useState(false);

  // Track the latest request to prevent race conditions
  const activeRequestRef = useRef<number>(0);
  const DEBOUNCE_MS = 600;

  // Debounced fetch when the IssueCast ID changes (non-empty only)
  useEffect(() => {
    const rawId = formData.issueCast?.audioStream?.locationStationId; // reused backing field
    const id = rawId?.trim() || '';

    if (!id) {
      activeRequestRef.current += 1; // invalidate any in-flight
      setIssueCastResult(null);
      setIssueCastError(null);
      setIssueCastLoading(false);
      return;
    }

    const requestMarker = activeRequestRef.current + 1;
    const timer = setTimeout(() => {
      activeRequestRef.current = requestMarker;
      setIssueCastLoading(true);
      setIssueCastError(null);
      setIssueCastResult(null);

      getIssueCastCapture(id)
        .then(data => {
          if (activeRequestRef.current === requestMarker) {
            setIssueCastResult(data);
          }
        })
        .catch(err => {
          if (activeRequestRef.current === requestMarker) {
            const message =
              (err?.response?.data && (err.response.data.message || err.response.data.error)) ||
              err?.message ||
              'Failed to fetch Podcast.';
            setIssueCastError(message);
            setIssueCastResult(null);
          }
        })
        .finally(() => {
          if (activeRequestRef.current === requestMarker) {
            setIssueCastLoading(false);
          }
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [formData.issueCast?.audioStream?.locationStationId]);

  // Keep a stable reference to onInputChange to avoid effect re-runs from changing function identity
  const onInputChangeRef = useRef(onInputChange);
  useEffect(() => {
    onInputChangeRef.current = onInputChange;
  }, [onInputChange]);

  // ---------------- Weather: geocoding support (Nominatim) ----------------
  const geoReqRef = useRef(0);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);

  useEffect(() => {
    const enabled = Boolean(formData.issueCast?.enabled && formData.issueCast?.weather?.enabled);
    const q = (formData.issueCast?.weather?.locationName || '').trim();
    if (!enabled || !q) {
      setGeoLoading(false);
      setGeoError(null);
      return;
    }

    const marker = geoReqRef.current + 1;
    const timer = setTimeout(() => {
      geoReqRef.current = marker;
      setGeoLoading(true);
      setGeoError(null);

      const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&addressdetails=1&q=${encodeURIComponent(q)}`;
      fetch(url, {
        headers: {
          'Accept': 'application/json',
        },
        method: 'GET',
      })
        .then(async res => {
          if (!res.ok) {
            throw new Error(`Geocoding failed (${res.status})`);
          }
          const data = await res.json();
          if (!Array.isArray(data) || data.length === 0) {
            throw new Error('No results found for the specified location.');
          }
          const first = data[0];
          const lat = String(first.lat ?? '');
          const lon = String(first.lon ?? '');

          if (geoReqRef.current === marker) {
            // Update lat/lon via stable ref to avoid triggering effect dependency on onInputChange
            onInputChangeRef.current?.({ target: { name: 'issueCast.weather.latitude', value: lat } } as unknown as React.ChangeEvent<HTMLInputElement>);
            onInputChangeRef.current?.({ target: { name: 'issueCast.weather.longitude', value: lon } } as unknown as React.ChangeEvent<HTMLInputElement>);
          }
        })
        .catch(err => {
          if (geoReqRef.current === marker) {
            setGeoError(err?.message || 'Failed to resolve location.');
          }
        })
        .finally(() => {
          if (geoReqRef.current === marker) {
            setGeoLoading(false);
          }
        });
    }, 600);

    return () => clearTimeout(timer);
  }, [formData.issueCast?.weather?.locationName, formData.issueCast?.weather?.enabled, formData.issueCast?.enabled]);

  // Derived helpers for weather lat/lon validation
  const latStr = String(formData.issueCast?.weather?.latitude ?? '');
  const lonStr = String(formData.issueCast?.weather?.longitude ?? '');
  const latNum = latStr ? Number(latStr) : NaN;
  const lonNum = lonStr ? Number(lonStr) : NaN;
  const latInvalid = latStr !== '' && (Number.isNaN(latNum) || latNum < -90 || latNum > 90);
  const lonInvalid = lonStr !== '' && (Number.isNaN(lonNum) || lonNum < -180 || lonNum > 180);

  // ---------------- Events: helpers ----------------
  const interestsRaw = (formData.issueCast?.events?.interests || '').trim();
  const interestsList = interestsRaw
    ? interestsRaw
        .split(',')
        .map(s => s.trim())
        .filter((s, idx, arr) => s.length > 0 && arr.indexOf(s) === idx)
    : [];

  const removeInterest = (value: string) => {
    const next = interestsList.filter(x => x.toLowerCase() !== value.toLowerCase());
    const nextStr = next.join(', ');
    onInputChange({ target: { name: 'issueCast.events.interests', value: nextStr } } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  // Extract publisherId from publisherPK for the ImageUrlSelector
  const publisherId = formData.publisherPK?.replace('PUBLISHER#', '') || '';

  // ---------------- Podcasts: helpers ----------------
  const podcastsList = formData.issueCast?.podcasts || [];
  
  const addPodcast = () => {
    const newPodcasts = [...podcastsList, { type: 'Spotify', url: '' }];
    onInputChange({ target: { name: 'issueCast.podcasts', value: newPodcasts } } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  const removePodcast = (index: number) => {
    const newPodcasts = podcastsList.filter((_, i) => i !== index);
    onInputChange({ target: { name: 'issueCast.podcasts', value: newPodcasts } } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  const updatePodcastType = (index: number, type: string) => {
    const newPodcasts = [...podcastsList];
    newPodcasts[index] = { ...newPodcasts[index], type };
    onInputChange({ target: { name: 'issueCast.podcasts', value: newPodcasts } } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  const updatePodcastUrl = (index: number, url: string) => {
    const newPodcasts = [...podcastsList];
    newPodcasts[index] = { ...newPodcasts[index], url };
    onInputChange({ target: { name: 'issueCast.podcasts', value: newPodcasts } } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  // ---------------- ConvAI: helpers ----------------
  const coHosts = formData.studioConfig?.coHosts || [];
  const coHostVoiceOptions = coHosts
    .map((ch, idx) => {
      const voiceId = (ch.voiceType === 'custom' ? ch.customVoiceId : ch.voiceId) || '';
      return {
        index: idx,
        name: (ch.name || '').trim() || `Co-host ${idx + 1}`,
        voiceId,
        disabled: !voiceId,
        helper:
          !voiceId
            ? ch.voiceType === 'journalist'
              ? 'Journalist voice type has no voiceId'
              : 'No voiceId set for this co-host'
            : '',
      };
    })
    .filter(x => x.name);

  const selectedConvaiVoiceId = formData.issueCast?.convai?.voiceId || '';

  // Validation for ConvAI
  const getConvAIStatus = (): { state: 'valid' | 'warning' | 'error'; missingFields: string[] } => {
    if (!formData.issueCast?.convai?.enabled) {
      return { state: 'valid', missingFields: [] };
    }

    const missingErrors: string[] = [];
    const missingWarnings: string[] = [];
    const convai = formData.issueCast.convai;

    if (!convai.voiceId) {
      missingErrors.push('Voice (select a co-host)');
    }

    if (!convai.firstMessage || !convai.firstMessage.trim()) {
      missingWarnings.push('First Message');
    }

    if (missingErrors.length > 0) {
      return { state: 'error', missingFields: [...missingErrors, ...missingWarnings] };
    }

    if (missingWarnings.length > 0) {
      return { state: 'warning', missingFields: missingWarnings };
    }

    return { state: 'valid', missingFields: [] };
  };

  const handleGenerateGradient = async () => {
    const logoUrl = formData.issueCast?.masthead?.publisherLogoURL;
    if (!logoUrl) return;

    setGeneratingGradient(true);
    try {
      const gradient = await generateGradientFromImage(logoUrl);
      console.log("Generated Gradient:", gradient); // Debug log
      onInputChange({ target: { name: 'issueCast.masthead.backgroundGradient', value: gradient } } as unknown as React.ChangeEvent<HTMLInputElement>);
    } catch (error: any) {
      console.error("Error generating gradient:", error);
      // Alert the user so they know it failed, instead of silently falling back to the blue gradient
      alert(`Failed to generate gradient: ${error.message || "Unknown error"}. Check console for details.`);
    } finally {
      setGeneratingGradient(false);
    }
  };

  const extractColors = (gradient: string) => {
    if (!gradient) return [];
    const matches = gradient.match(/#[0-9a-fA-F]{6}|#[0-9a-fA-F]{3}/g);
    return matches ? matches.slice(0, 2) : [];
  };

  const gradientColors = extractColors(formData.issueCast?.masthead?.backgroundGradient || '');

  return (
    <Grid container spacing={3}>
      {/* Header with master on/off */}
      <Grid item xs={12}>
        <Paper
          elevation={1}
          sx={{
            p: 3,
            mb: 2,
            backgroundColor: theme => theme.palette.background.default,
            border: theme => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PodcastsIcon sx={{ color: 'primary.main', mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Configure Podcast for this news source.
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(formData.issueCast?.enabled)}
                  onChange={onIssueCastEnabledChange}
                  name="issueCast.enabled"
                  disabled={isLoading}
                />
              }
              label={
                <Typography variant="body1" fontWeight="medium">
                  Podcast Enabled
                </Typography>
              }
            />
          </Box>
        </Paper>
      </Grid>

      {/* Masthead Section */}
      <Grid item xs={12}>
        <CollapsibleSection title="Masthead" icon={<BrandingWatermarkIcon sx={{ mr: 1 }} />} namespace="issuecast-tab" id="masthead">
          <Typography variant="body2" color="text.secondary" paragraph>
            Provide branding details used across Podcast players, feeds, and embeds.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ImageUrlSelector
                label="Publisher Logo URL"
                name="issueCast.masthead.publisherLogoURL"
                value={formData.issueCast?.masthead?.publisherLogoURL || ''}
                onChange={onInputChange}
                disabled={isLoading || !formData.issueCast?.enabled}
                publisherId={publisherId}
                placeholder="https://..."
                helperText="Public image URL shown in the Podcast player and shared embeds"
                backgroundGradient={formData.issueCast?.masthead?.backgroundGradient}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  label="Background Gradient (CSS)"
                  name="issueCast.masthead.backgroundGradient"
                  value={formData.issueCast?.masthead?.backgroundGradient || ''}
                  onChange={onInputChange}
                  disabled={isLoading || !formData.issueCast?.enabled}
                  placeholder="linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)"
                  helperText="CSS gradient string for the player background"
                  InputProps={{
                    endAdornment: (
                      <IconButton 
                        onClick={handleGenerateGradient}
                        disabled={isLoading || !formData.issueCast?.enabled || !formData.issueCast?.masthead?.publisherLogoURL || generatingGradient}
                        title="Auto-generate from Logo"
                        edge="end"
                      >
                        <AutoFixHighIcon />
                      </IconButton>
                    )
                  }}
                />
                <Stack spacing={1}>
                  <Paper
                    elevation={3}
                    sx={{
                      width: 56,
                      height: 56,
                      flexShrink: 0,
                      background: formData.issueCast?.masthead?.backgroundGradient || '#eee',
                      borderRadius: 1,
                      border: '1px solid #ccc'
                    }}
                    title="Preview"
                  />
                  {gradientColors.map((color, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 16, height: 16, bgcolor: color, border: '1px solid #ccc', borderRadius: 0.5 }} />
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>{color}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Location Name"
                name="issueCast.masthead.locationName"
                value={formData.issueCast?.masthead?.locationName || ''}
                onChange={onInputChange}
                disabled={isLoading || !formData.issueCast?.enabled}
                placeholder="Coachella Valley"
                error={Boolean(errors['issueCast.masthead.locationName'])}
                helperText={
                  errors['issueCast.masthead.locationName'] ||
                  "The name locals use to describe the area. Used in the weather widget and elsewhere."
                }
              />
            </Grid>
          </Grid>
        </CollapsibleSection>
      </Grid>

      {/* Sponsor Section */}
      <Grid item xs={12}>
        <CollapsibleSection title="Sponsor" icon={<LoyaltyIcon sx={{ mr: 1 }} />} namespace="issuecast-tab" id="sponsor">
          <Typography variant="body2" color="text.secondary" paragraph>
            Configure optional sponsor details displayed in the native ad (not referenced in the audio).
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ImageUrlSelector
                label="Sponsor Image URL"
                name="issueCast.sponsor.imageUrl"
                value={formData.issueCast?.sponsor?.imageUrl || ''}
                onChange={onInputChange}
                disabled={isLoading || !formData.issueCast?.enabled}
                publisherId={publisherId}
                placeholder="https://..."
                helperText="Image URL shown in the native ad within the Podcast player"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Sponsor Click Through URL"
                type="url"
                name="issueCast.sponsor.clickThroughUrl"
                value={formData.issueCast?.sponsor?.clickThroughUrl || ''}
                onChange={onInputChange}
                disabled={isLoading || !formData.issueCast?.enabled}
                placeholder="https://sponsor.example.com/landing?utm_source=issuecast"
                helperText="This is the URL that will open in a new browser tab when the user clicks or taps the native ad in the Podcast player"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Sponsor Name"
                name="issueCast.sponsor.name"
                value={formData.issueCast?.sponsor?.name || ''}
                onChange={onInputChange}
                disabled={isLoading || !formData.issueCast?.enabled}
                placeholder="e.g., Acme Coffee"
                helperText="Name of the sponsor displayed in the Podcast player"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Sponsor Mention"
                name="issueCast.sponsor.mention"
                value={formData.issueCast?.sponsor?.mention || ''}
                onChange={onInputChange}
                disabled={isLoading || !formData.issueCast?.enabled}
                placeholder="e.g., 'Today's headlines are brought to you by Acme Coffee'"
                helperText="Sponsor message for native advertising within the Podcast player"
              />
            </Grid>
          </Grid>
        </CollapsibleSection>
      </Grid>

      {/* Audio Stream Section */}
      <Grid item xs={12}>
        <CollapsibleSection title="Audio Stream" icon={<GraphicEqIcon sx={{ mr: 1 }} />} namespace="issuecast-tab" id="Audio Stream">
          <Typography variant="body2" color="text.secondary" paragraph>
            Configure the audio stream settings for Podcast.
          </Typography>
          <Grid container spacing={2}>
            {/* Publisher Subdomain moved here, to the left of Podcast ID */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Publisher Subdomain"
                name="issueCast.masthead.publisherSubdomain"
                value={formData.issueCast?.masthead?.publisherSubdomain || ''}
                onChange={onInputChange}
                disabled={isLoading || !formData.issueCast?.enabled}
                placeholder="e.g., kenwood-press"
                error={Boolean(errors['issueCast.masthead.publisherSubdomain'])}
                helperText={
                  errors['issueCast.masthead.publisherSubdomain'] ||
                  'Subdomain used for the Podcast web player URL'
                }
                inputProps={{
                  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === ' ') {
                      const { selectionStart, selectionEnd, value } = e.currentTarget;
                      const len = value ? value.length : 0;
                      const start = selectionStart ?? 0;
                      const end = selectionEnd ?? 0;
                      const atStart = start === 0 && end === 0;
                      const atEnd = start === len && end === len;
                      if (atStart || atEnd) {
                        e.preventDefault();
                      }
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Podcast ID"
                name="issueCast.audioStream.locationStationId"
                value={formData.issueCast?.audioStream?.locationStationId || ''}
                onChange={onInputChange}
                disabled={isLoading || !formData.issueCast?.enabled}
                placeholder="ic_12345"
                error={Boolean(errors['issueCast.audioStream.locationStationId'])}
                helperText={
                  errors['issueCast.audioStream.locationStationId'] ||
                  'Enter the Podcast ID to validate and view when it was created.'
                }
              />
              {issueCastLoading && (
                <Box mt={1}>
                  <Typography variant="body2" color="text.secondary">Validating Podcast…</Typography>
                </Box>
              )}
              {issueCastError && (
                <Box mt={1}>
                  <Alert severity="error">{issueCastError}</Alert>
                </Box>
              )}
            </Grid>

            {/* Validation result: show created date in local timezone and link to Podcast page */}
            {issueCastResult?.issueCast && !issueCastError && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="success.main">
                    Valid Podcast ID
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Created At (your local time)"
                    value={(() => {
                      const ts = issueCastResult?.issueCast?.createdAt;
                      try { return ts ? new Date(ts).toLocaleString() : ''; } catch { return ts || ''; }
                    })()}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  {(() => {
                    const sub = (formData.issueCast?.masthead?.publisherSubdomain || '').trim();
                    const href = sub ? `https://${sub}.locationstation.net/` : '';
                    return sub ? (
                      <Box sx={{ mt: { xs: 1, md: 0 } }}>
                        <Typography variant="body2">
                          <a href={href} target="_blank" rel="noopener noreferrer">
                            Open Podcast page on {sub}.locationstation.net
                          </a>
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: { xs: 1, md: 0 } }}>
                        Enter Publisher Subdomain to enable the Podcast page link.
                      </Typography>
                    );
                  })()}
                </Grid>
              </>
            )}
          </Grid>
        </CollapsibleSection>
      </Grid>

      {/* Weather Section */}
      <Grid item xs={12}>
        <CollapsibleSection title="Weather" icon={<WbSunnyIcon sx={{ mr: 1 }} />} namespace="issuecast-tab" id="weather">
          {/* Weather master toggle */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Select a location and coordinates for weather forecasts.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(formData.issueCast?.weather?.enabled)}
                  onChange={(e) => onInputChange({ target: { name: 'issueCast.weather.enabled', value: e.target.checked } } as unknown as React.ChangeEvent<HTMLInputElement>)}
                  name="issueCast.weather.enabled"
                  disabled={isLoading || !formData.issueCast?.enabled}
                />
              }
              label={<Typography variant="body2">Weather Enabled</Typography>}
            />
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Weather Location"
                name="issueCast.weather.locationName"
                value={formData.issueCast?.weather?.locationName || ''}
                onChange={onInputChange}
                disabled={isLoading || !formData.issueCast?.enabled || !formData.issueCast?.weather?.enabled}
                placeholder="Kenwood, CA"
                helperText={geoError ? geoError : geoLoading ? 'Resolving location…' : 'Enter a place name; coordinates will auto-fill via OpenStreetMap Nominatim'}
                error={Boolean(geoError)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Latitude"
                name="issueCast.weather.latitude"
                value={latStr}
                onChange={onInputChange}
                disabled={isLoading || !formData.issueCast?.enabled || !formData.issueCast?.weather?.enabled}
                placeholder="38.4378"
                error={latInvalid}
                helperText={latInvalid ? 'Latitude must be a number between -90 and 90' : ''}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Longitude"
                name="issueCast.weather.longitude"
                value={lonStr}
                onChange={onInputChange}
                disabled={isLoading || !formData.issueCast?.enabled || !formData.issueCast?.weather?.enabled}
                placeholder="-122.5482"
                error={lonInvalid}
                helperText={lonInvalid ? 'Longitude must be a number between -180 and 180' : ''}
              />
            </Grid>
          </Grid>
        </CollapsibleSection>
      </Grid>

      {/* Events Section */}
      <Grid item xs={12}>
        <CollapsibleSection title="Events" icon={<EventIcon sx={{ mr: 1 }} />} namespace="issuecast-tab" id="events">
          {/* Events master toggle */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Select interests to personalize the events feed.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(formData.issueCast?.events?.enabled)}
                  onChange={(e) => onInputChange({ target: { name: 'issueCast.events.enabled', value: e.target.checked } } as unknown as React.ChangeEvent<HTMLInputElement>)}
                  name="issueCast.events.enabled"
                  disabled={isLoading || !formData.issueCast?.enabled}
                />
              }
              label={<Typography variant="body2">Events Enabled</Typography>}
            />
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Interests (comma-separated)"
                name="issueCast.events.interests"
                value={formData.issueCast?.events?.interests || ''}
                onChange={onInputChange}
                disabled={isLoading || !formData.issueCast?.enabled || !formData.issueCast?.events?.enabled}
                placeholder="food & drink, family, live music, art"
                helperText="Enter interests separated by commas"
              />
            </Grid>
            {/* New checkboxes for events sources */}
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(formData.issueCast?.events?.usePublisherEvents)}
                    onChange={(e) => onInputChange({ target: { name: 'issueCast.events.usePublisherEvents', value: e.target.checked } } as unknown as React.ChangeEvent<HTMLInputElement>)}
                    name="issueCast.events.usePublisherEvents"
                    disabled={isLoading || !formData.issueCast?.enabled || !formData.issueCast?.events?.enabled}
                  />
                }
                label={<Typography variant="body2">Use Publisher Events</Typography>}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(formData.issueCast?.events?.useSearchEvents)}
                    onChange={(e) => onInputChange({ target: { name: 'issueCast.events.useSearchEvents', value: e.target.checked } } as unknown as React.ChangeEvent<HTMLInputElement>)}
                    name="issueCast.events.useSearchEvents"
                    disabled={isLoading || !formData.issueCast?.enabled || !formData.issueCast?.events?.enabled}
                  />
                }
                label={<Typography variant="body2">Use Search Events</Typography>}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Use Publisher Events includes events curated or imported for this publisher. Use Search Events includes events discovered from external sources for your locale. When both are off, only your selected interests are used.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {interestsList.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => removeInterest(tag)}
                    disabled={isLoading || !formData.issueCast?.enabled || !formData.issueCast?.events?.enabled}
                    sx={{ mb: 1 }}
                  />
                ))}
                {interestsList.length === 0 && (
                  <Typography variant="body2" color="text.secondary">No interests yet</Typography>
                )}
              </Stack>
            </Grid>
          </Grid>
        </CollapsibleSection>
      </Grid>

      {/* ConvAI Section */}
      <Grid item xs={12}>
        <CollapsibleSection
          title="Conversational AI"
          icon={<PodcastsIcon sx={{ mr: 1 }} />}
          namespace="issuecast-tab"
          id="convai"
          status={{
            enabled: Boolean(formData.issueCast?.convai?.enabled),
            ...getConvAIStatus()
          }}
        >
          <Typography variant="body2" color="text.secondary" paragraph>
            Configure the conversational AI that speaks with listeners in real time.
          </Typography>

          {/* ConvAI master toggle */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(formData.issueCast?.convai?.enabled)}
                  onChange={(e) => {
                    const next = e.target.checked;
                    onInputChange({ target: { name: 'issueCast.convai.enabled', value: next } } as unknown as React.ChangeEvent<HTMLInputElement>);
                    if (!next) {
                      // Clear voiceId when disabling to avoid stale selections
                      onInputChange({ target: { name: 'issueCast.convai.voiceId', value: '' } } as unknown as React.ChangeEvent<HTMLInputElement>);
                    }
                  }}
                  name="issueCast.convai.enabled"
                  disabled={isLoading || !formData.issueCast?.enabled}
                />
              }
              label={<Typography variant="body2">Conversational AI Enabled</Typography>}
            />
            <Typography variant="body2" color="text.secondary">
              Enable conversational AI and select which co-host voice should be used.
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl
                component="fieldset"
                disabled={isLoading || !formData.issueCast?.enabled || !formData.issueCast?.convai?.enabled}
              >
                <FormLabel component="legend">Voice (select a co-host)</FormLabel>
                <RadioGroup
                  value={selectedConvaiVoiceId}
                  onChange={(e) =>
                    onInputChange({ target: { name: 'issueCast.convai.voiceId', value: e.target.value } } as unknown as React.ChangeEvent<HTMLInputElement>)
                  }
                >
                  {coHostVoiceOptions.map(opt => (
                    <FormControlLabel
                      key={`${opt.index}-${opt.voiceId || 'novoice'}`}
                      value={opt.voiceId}
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body2">{opt.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{opt.voiceId ? `voiceId: ${opt.voiceId}` : opt.helper}</Typography>
                        </Box>
                      }
                      disabled={opt.disabled}
                    />
                  ))}
                </RadioGroup>
                <FormHelperText>
                  {coHostVoiceOptions.length === 0
                    ? 'No co-hosts found. Configure co-hosts in the Studio tab first.'
                    : 'ConvAI voiceId must match a configured co-host voiceId.'}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="First Message"
                name="issueCast.convai.firstMessage"
                value={formData.issueCast?.convai?.firstMessage || ''}
                onChange={onInputChange}
                disabled={isLoading || !formData.issueCast?.enabled || !formData.issueCast?.convai?.enabled}
                placeholder="What's up?"
                helperText="This is what the agent will say immediately after a listener connects."
                multiline
                minRows={2}
              />
            </Grid>

            {formData.issueCast?.convai?.enabled && !selectedConvaiVoiceId && (
              <Grid item xs={12}>
                <Alert severity="warning">Select a co-host voice for ConvAI (voiceId).</Alert>
              </Grid>
            )}
          </Grid>
        </CollapsibleSection>
      </Grid>

      {/* Podcasts Section */}
      <Grid item xs={12}>
        <CollapsibleSection title="Podcasts" icon={<HeadsetIcon sx={{ mr: 1 }} />} namespace="issuecast-tab" id="podcasts">
          <Typography variant="body2" color="text.secondary" paragraph>
            Add links to podcast platforms where your Podcast is available.
          </Typography>
          <Grid container spacing={2}>
            {podcastsList.map((podcast, index) => (
              <React.Fragment key={index}>
                <Grid item xs={16} md={4}>
                  <Select
                    fullWidth
                    size="small"
                    value={podcast.type}
                    onChange={(e) => updatePodcastType(index, e.target.value)}
                    disabled={isLoading || !formData.issueCast?.enabled}
                  >
                    <MenuItem value="Spotify">Spotify</MenuItem>
                    <MenuItem value="Apple Podcasts">Apple Podcasts</MenuItem>
                  </Select>
                </Grid>
                <Grid item xs={12} md={7}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Podcast URL"
                    value={podcast.url}
                    onChange={(e) => updatePodcastUrl(index, e.target.value)}
                    disabled={isLoading || !formData.issueCast?.enabled}
                    placeholder="https://..."
                  />
                </Grid>
                <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton
                    color="error"
                    onClick={() => removePodcast(index)}
                    disabled={isLoading || !formData.issueCast?.enabled}
                    size="small"
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Grid>
              </React.Fragment>
            ))}
            <Grid item xs={12}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddCircleOutlineIcon />}
                onClick={addPodcast}
                disabled={isLoading || !formData.issueCast?.enabled}
                sx={{ alignSelf: 'flex-start' }}
              >
                Add Podcast
              </Button>
            </Grid>
            {podcastsList.length === 0 && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  No podcasts added yet. Click "Add Podcast" to get started.
                </Typography>
              </Grid>
            )}
          </Grid>
        </CollapsibleSection>
      </Grid>
    </Grid>
  );
};

export default IssueCastTab;

const generateGradientFromImage = (url: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Only set crossOrigin for remote URLs to avoid issues with data URIs or local files
    if (!url.startsWith('data:') && !url.startsWith('blob:')) {
      img.crossOrigin = "Anonymous";
    }
    img.src = url;

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas context not available');

        // 1. Resize: Use a slightly larger size for better sampling
        const width = 100;
        const height = 100;
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        let imageData;
        try {
            imageData = ctx.getImageData(0, 0, width, height).data;
        } catch (e) {
            throw new Error("CORS restriction: Cannot access image data. Ensure the image server allows CORS (Access-Control-Allow-Origin).");
        }
        
        // 2. Quantization Step
        const quantization = 20; 

        // Helper to count colors with optional filtering
        const countColors = (filter: boolean) => {
            const counts: Record<string, { count: number, r: number, g: number, b: number }> = {};
            for (let i = 0; i < imageData.length; i += 4) {
                const r = imageData[i];
                const g = imageData[i + 1];
                const b = imageData[i + 2];
                const a = imageData[i + 3];

                if (a < 128) continue; // Always skip transparent
                
                if (filter) {
                    // Filter out near-white and near-black
                    if (r > 250 && g > 250 && b > 250) continue; 
                    if (r < 10 && g < 10 && b < 10) continue;    
                }

                const roundedR = Math.floor(r / quantization) * quantization;
                const roundedG = Math.floor(g / quantization) * quantization;
                const roundedB = Math.floor(b / quantization) * quantization;
                
                const key = `${roundedR},${roundedG},${roundedB}`;
                if (!counts[key]) {
                    counts[key] = { count: 0, r: roundedR, g: roundedG, b: roundedB };
                }
                counts[key].count++;
            }
            return counts;
        };

        // First pass: Try with filters (ignore white/black)
        let colorCounts = countColors(true);
        let sortedColors = Object.values(colorCounts).sort((a, b) => b.count - a.count);

        // Second pass: If no colors found (e.g. image is all black/white), try without filters
        if (sortedColors.length === 0) {
            console.warn("No colors found with filters, retrying without filters.");
            colorCounts = countColors(false);
            sortedColors = Object.values(colorCounts).sort((a, b) => b.count - a.count);
        }

        if (sortedColors.length === 0) {
           throw new Error("No opaque pixels found in image.");
        }

        // Log top colors for debugging
        console.log("Top extracted colors:", sortedColors.slice(0, 5).map(c => `rgb(${c.r},${c.g},${c.b}) count:${c.count}`));

        // 4. Select Primary Color
        const primary = sortedColors[0];
        const primaryHex = rgbToHex(primary.r, primary.g, primary.b);

        // 5. Select Secondary Color
        let secondaryHex = primaryHex;
        const minDistance = 60; 

        for (let i = 1; i < sortedColors.length; i++) {
          const current = sortedColors[i];
          
          const dist = Math.sqrt(
            Math.pow(current.r - primary.r, 2) + 
            Math.pow(current.g - primary.g, 2) + 
            Math.pow(current.b - primary.b, 2)
          );

          if (dist > minDistance) {
            secondaryHex = rgbToHex(current.r, current.g, current.b);
            break;
          }
        }
        
        if (primaryHex === secondaryHex) {
            // If no distinct secondary color, create a variation
            secondaryHex = adjustBrightness(primary.r, primary.g, primary.b, -40);
        }

        resolve(`linear-gradient(135deg, ${primaryHex} 0%, ${secondaryHex} 100%)`);
      } catch (e: any) {
        reject(e);
      }
    };

    img.onerror = () => reject(new Error("Failed to load image. Check URL or file path."));
  });
};

// Helper: Convert RGB numbers to Hex string
const rgbToHex = (r: number, g: number, b: number): string => {
  const toHex = (c: number) => {
    const hex = Math.min(255, Math.max(0, c)).toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

// Helper: Darken or Lighten a color (amount can be negative to darken)
const adjustBrightness = (r: number, g: number, b: number, amount: number) => {
    return rgbToHex(r + amount, g + amount, b + amount);
};
